import React, { useState, useCallback } from "react";
import ProgressTracker from "../ProgressTracker/ProgressTracker";
import { formatDate } from "../../../helpers/DateFormatter";
import { LogProps } from "../../../interfaces/ILogs";
import { Download } from "react-bootstrap-icons";
import { getBlobData } from "../../../services/logsService";
import "./AccordionRow.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spinner } from "react-bootstrap";
import { containerNamesByPipeline } from "../../../utils/constants"
import { getTaskNameFromPipeline } from "../../../helpers/TaskNames";

const AccordionRow: React.FC<LogProps> = ({ log }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

   const getFilenameFromBlob = (blobName: string): string => {
    if (!blobName) return "";
    
    const parts = blobName.split('/');
    return parts[parts.length - 1];
  };

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);

    const blobName = log.BlobName || "downloaded-file.xml";
    const taskName = getTaskNameFromPipeline(log.TaskName);

    if (taskName === null) {
      toast.error(`Unknown pipeline: ${log.TaskName}`);
      setIsDownloading(false);
      return;
    }

    try {
      const blob = await getBlobData(log.BlobUrl);

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = blobName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);

      toast.success("Download started successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file. File unavailable.");
    } finally {
      setIsDownloading(false);
    }
  }, [log.BlobName, log.TaskName]);

  const downloadablePipelines = ["CalculateUsage", "ParseXmltoDB", "Withdrawal", "Passthrough", "MissingMTC", "DayAheadParseCsv"];
  const shouldShowDownload = downloadablePipelines.includes(log?.TaskName);

  return (
    <>
      <tr
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: "pointer" }}
        className={isOpen ? "table-active" : "table-collapsed"}
      >
        <td>{formatDate(log.LatestTimestamp)}</td>
        <td>{getFilenameFromBlob(log.BlobName)}</td> 
        <td>{log.TaskName}</td>
        <td>{log.LatestStatus}</td>
        <td className="text-center">
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} />
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={5}>
            <div className="p-3 accordion-content">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-subtitle mb-3 text-muted">Progress Details</h6>
                  {log.Logs && <ProgressTracker logs={log.Logs} taskName={log.TaskName} />}

                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <small className="text-muted">
                      <strong>Message:</strong> {log.Description}
                    </small>
                    {shouldShowDownload && (
                      <button
                        className="btn-download d-flex align-items-center text-primary"
                        onClick={handleDownload}
                        aria-label="Download file"
                        disabled={isDownloading}
                      >
                        {isDownloading ? <Spinner animation="border" size="sm" className="me-2" /> : <Download size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default AccordionRow;
